import {
  formatRiskTrendPct,
  getSegmentRiskTrendPct,
} from './road-risk-map-trend'
import { RISK_MAP_SCORE_DECIMALS, riskScorePerKm } from '../shared/constants/risk-map'

/**
 * Build and download a CSV of scored road risk segments (riskScore desc).
 * Scores are exported per km (API stores per meter).
 * @param {object} featureCollection GeoJSON FeatureCollection from risk-map API
 * @param {Map<number, number>|null} [previousScoresBySegmentId] scores for previous year
 * @param {number|null} [year] calendar year included in the download filename
 */
export function downloadRoadRiskMapCsv(
  featureCollection,
  previousScoresBySegmentId = null,
  year = null
) {
  const features = Array.isArray(featureCollection?.features)
    ? [...featureCollection.features]
    : []

  features.sort(
    (a, b) => Number(b?.properties?.riskScore ?? 0) - Number(a?.properties?.riskScore ?? 0)
  )

  const headers = [
    'segmentId',
    'name',
    'lengthM',
    'riskScorePerKm',
    'trendPct',
    'crashCount',
    'fatalCount',
    'seriousCount',
    'slightCount',
    'noInjuryCount',
    'unknownCount',
    'centerLat',
    'centerLon',
  ]

  const escapeCell = (value) => {
    if (value == null) return ''
    const str = String(value)
    if (/[",\n\r]/.test(str)) return `"${str.replace(/"/g, '""')}"`
    return str
  }

  const formatScoreCell = (value) => riskScorePerKm(value).toFixed(RISK_MAP_SCORE_DECIMALS)

  const rows = features.map((f) => {
    const p = f?.properties || {}
    const score = Number(p.riskScore ?? 0)
    const trendPct = getSegmentRiskTrendPct(previousScoresBySegmentId, p.segmentId, score)
    return [
      p.segmentId,
      p.name ?? '',
      p.lengthM,
      formatScoreCell(score),
      formatRiskTrendPct(trendPct),
      p.crashCount,
      p.fatalCount,
      p.seriousCount,
      p.slightCount,
      p.noInjuryCount,
      p.unknownCount,
      p.centerLat,
      p.centerLon,
    ].map(escapeCell).join(',')
  })

  const csv = [headers.join(','), ...rows].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const yearPart = Number.isFinite(Number(year)) ? `-${Number(year)}` : ''
  link.download = `road-risk-map${yearPart}-${new Date().toISOString().slice(0, 10)}.csv`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
